import { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "react-calendar/dist/Calendar.css";

import { SearchButton } from "@/components/common/buttons";
import { LocationDropdown, CalendarInput, TrailerTypeInput, RadiusInput } from "@/components/search";
import { cn } from "@/utils/css";
import { setInputSearch, clearInputs, setPickup, setPickupQuery, setDelivery, setDeliveryQuery } from "@/state/searchInputSlice";
import { clearCurrentSearch } from "@/state/currentSearchSlice";
import { useQuery } from "@/utils/query";

export const SearchInputs = memo(({ className = "" }) => {
    const { searchType, pickup, delivery, pickupQuery, deliveryQuery, pickupRadius, deliveryRadius } = useSelector(
        (state) => state.searchInput,
    );
    const {
        pickupZip,
        pickupPlaceName,
        pickupStateCode,
        pickupWithZip,
        deliveryZip,
        deliveryPlaceName,
        deliveryStateCode,
        deliveryWithZip,
    } = useQuery();
    const isMarketPotential = searchType === "market_potential";
    const isLoadRate = searchType === "load_rates";
    const isLoadboard = searchType === "loadboard";
    const dispatch = useDispatch();
    const hasDelivery = isLoadboard || isLoadRate;

    const handleSwitch = () => {
        dispatch(
            setInputSearch({
                pickup: delivery,
                delivery: pickup,
                pickupQuery: deliveryQuery,
                deliveryQuery: pickupQuery,
                pickupRadius: deliveryRadius,
                deliveryRadius: pickupRadius,
            }),
        );
    };

    useEffect(() => {
        return () => {
            dispatch(clearInputs());
            dispatch(clearCurrentSearch());
        };
    }, []);

    return (
        <div className={cn("w-full md:justify-center min-h-12 flex flex-col md:flex-row mt-4 p-6 pt-0 md:p-0 md:pb-4", className)}>
            <TrailerTypeInput />
            <LocationDropdown
                className={cn("transition-minWidth ease-in-out duration-50 w-full md:max-w-[200px]", {
                    "mb-1 md:mb-0 md:mr-0": hasDelivery,
                    "lg:max-w-[264px]": isLoadRate,
                })}
                label={hasDelivery ? "Pickup" : "Location"}
                width={isLoadRate ? 264 : 200}
                handleSwitch={isLoadRate ? handleSwitch : undefined}
                name="pickup"
                value={pickup}
                setValue={(value) => dispatch(setPickup(value))}
                query={pickupQuery}
                setQuery={(query) => dispatch(setPickupQuery(query))}
                urlQueryValues={{
                    zip: pickupZip,
                    placeName: pickupPlaceName,
                    stateCode: pickupStateCode,
                    withZip: pickupWithZip,
                }}
            />
            <RadiusInput
                type="pickup"
                visible={isMarketPotential || isLoadboard}
                className={cn({
                    "mr-1": isMarketPotential,
                })}
                handleSwitch={isLoadboard ? handleSwitch : undefined}
            />
            <LocationDropdown
                className={cn("transition-all ease-in-out duration-50 w-full h-[64px]", {
                    "md:max-w-[200px] mb-1 md:mb-0 overflow-visible": hasDelivery,
                    "lg:max-w-[264px] md:mr-1": isLoadRate,
                    "min-w-0 max-w-0 overflow-hidden max-h-0": !hasDelivery,
                })}
                label="Delivery"
                width={isLoadRate ? 264 : isMarketPotential ? 0 : 200}
                name="delivery"
                value={delivery}
                setValue={(value) => dispatch(setDelivery(value))}
                query={deliveryQuery}
                setQuery={(query) => dispatch(setDeliveryQuery(query))}
                urlQueryValues={{
                    zip: deliveryZip,
                    placeName: deliveryPlaceName,
                    stateCode: deliveryStateCode,
                    withZip: deliveryWithZip,
                }}
            />
            <RadiusInput
                type="delivery"
                visible={isLoadboard}
                className={cn({
                    "mr-1": isLoadboard,
                })}
            />
            <CalendarInput name="pickup" isLastInput={!isLoadboard} />
            <CalendarInput
                name="delivery"
                isLastInput
                className={cn("transition-all ease-in-out duration-50", {
                    "md:max-w-[0px] overflow-hidden opacity-0": !isLoadboard,
                    "md:max-w-[200px] overflow-visible opacity-100": isLoadboard,
                })}
            />
            <div className="relative w-full md:w-[78px] flex md:ml-4 bg-cm-p-surface-container">
                <SearchButton />
            </div>
        </div>
    );
});
